import React, { useState } from 'react';
import MagicForm from '../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../Enums/MagicField.enum';
import {
	useLazyQuestionnaireSchedulesByParentScheduleQuery,
	useLazyQuestionnaireSearchByCodeQuery,
} from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import useTranslation from '../../../../Hooks/useTranslation';
import apiClient from '../../../../Services/ApiClient';
import Loading from '../../../../Components/Loading';
import { QuestionnaireModule } from '../../Enums/QuestionnaireModule.enum';

interface QuestionnairePlanningPeriodicalFormProps {
	onSubmit: (values: any) => void;
	defaultData?: any;
	addDoctor: boolean;
	module?: QuestionnaireModule;
}

export default function QuestionnairePlanningPeriodicalForm(
	props: QuestionnairePlanningPeriodicalFormProps,
) {
	const { defaultData: parentDefaultData, onSubmit, addDoctor, module } = props;
	const [getQuestionnaireByCode] = useLazyQuestionnaireSearchByCodeQuery();
	const [getQuestionnaireScheduleByParentSchedule] =
		useLazyQuestionnaireSchedulesByParentScheduleQuery();
	const [esasId, setEsasId] = useState<string>('');
	const [treeItems, setTreeItems] = useState<{ id: string; title: string }[]>(
		[],
	);
	const [defaultData, setDefaultData] = useState(parentDefaultData);
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(true);

	useEffectAsync(async () => {
		const { questionnaire } = await getQuestionnaireByCode({
			code: 'esas',
		}).unwrap();
		if (questionnaire?.id) {
			setEsasId(questionnaire.id);

			const { data: result } = await apiClient.get(
				'questionnaire/' + questionnaire.id + '/tree',
			);
			setTreeItems(result);

			const items: {
				[key: string]: { repeatHours: { value: string; label?: string } };
			} = {};
			let frequencyType = '1';

			if (questionnaire.id === defaultData?.questionnaire?.value) {
				const { questionnaireSchedules } =
					await getQuestionnaireScheduleByParentSchedule({
						parentQuestionnaireScheduleId: defaultData.id,
					}).unwrap();

				const childSchedules = questionnaireSchedules?.data ?? [];
				if (childSchedules.length) {
					frequencyType = '0';
				}
			}

			setDefaultData({ ...defaultData, frequencyType, items });
		}

		setIsLoading(false);
	});

	const handleSubmit = async (values: any) => {
		onSubmit(values);
	};

	const formSchemePrepend: ItemScheme[] = [];
	if (addDoctor) {
		formSchemePrepend.push({
			type: MagicField.DOCTOR_SELECT,
			key: 'doctor',
			title: 'component.questionnaires.fromDoctor',
			settings: {
				required: true,
			},
		});
	}

	let formScheme: ItemScheme[] = [
		{
			type: MagicField.PATIENT_SELECT,
			key: 'patient',
			title: 'component.questionnaires.forPatient',
			settings: {
				required: true,
			},
		},
		{
			type: MagicField.WORKPLACE_SELECT,
			key: 'workplace',
			title: 'base.shared.workplace',
			settings: {
				required: true,
			},
		},
		{
			type: MagicField.QUESTIONNAIRE_SELECT,
			key: 'questionnaire',
			title: 'base.shared.questionnaire',
			settings: {
				required: true,
				enablePicker: true,
				module: module,
			},
		},
		{
			type: MagicField.CONDITION,
			settings: {
				rules: [
					{
						rule: 'equal',
						value: esasId,
						formControlKey: 'questionnaire',
					},
				],
			},
			subType: 'view',
			children: [
				{
					type: MagicField.RADIOLIST,
					key: 'frequencyType',
					title: 'component.questionnaires.frequency',
					settings: {
						required: true,
					},
					inLine: true,
					children: [
						{
							key: '0',
							value: '0',
							title: 'component.questionnaires.individually',
						},
						{
							key: '1',
							value: '1',
							title: 'component.questionnaires.inSameTimes',
						},
					],
				},
				{
					type: MagicField.CONDITION,
					value: 'frequencyType',
					settings: {
						equal: '0',
					},
					subType: 'view',
					children: [
						treeItems.length
							? {
									type: MagicField.CONTAINER,
									key: 'items',
									settings: {
										showLine: true,
									},
									children: treeItems.map(treeItem => {
										return {
											type: MagicField.CONTAINER,
											key: treeItem.id,
											children: [
												{
													type: MagicField.HSTACK,
													settings: {
														alignCenter: true,
													},
													children: [
														{
															type: MagicField.TEXT,
															subType: 'subtitle',
															title: treeItem.title,
														},
														{
															type: MagicField.QUESTIONNAIRE_SCHEDULE_FREQUENCY,
															key: 'repeatHours',
															title: '',
															settings: {
																required: true,
															},
														},
													],
												},
												{
													type: MagicField.HR,
												},
											],
										};
									}),
							  }
							: {
									type: MagicField.TEXT,
									title: 'base.shared.loading',
							  },
					],
				},
				{
					type: MagicField.CONDITION,
					value: 'frequencyType',
					settings: {
						equal: '1',
					},
					subType: 'view',
					children: [
						{
							type: MagicField.CONTAINER,
							settings: {
								showLine: true,
							},
							children: [
								{
									type: MagicField.QUESTIONNAIRE_SCHEDULE_FREQUENCY,
									key: 'repeatHours',
									title: 'component.questionnaires.frequency',
								},
							],
						},
					],
				},
			],
		},
		{
			type: MagicField.CONDITION,
			settings: {
				rules: [
					{
						rule: 'notEqual',
						value: esasId,
						formControlKey: 'questionnaire',
					},
				],
			},
			subType: 'view',
			children: [
				{
					type: MagicField.QUESTIONNAIRE_SCHEDULE_FREQUENCY,
					key: 'repeatHours',
					title: 'component.questionnaires.frequency',
				},
			],
		},
		{
			type: MagicField.HSTACK,
			children: [
				{
					type: MagicField.DATEPICKER,
					key: 'dateFrom',
					title: 'component.questionnaires.firstSentDate',
					settings: {
						required: true,
						rules: [
							{
								rule: 'min',
								value: 'today',
							},
						],
					},
				},
				{
					type: MagicField.DATEPICKER,
					key: 'dateTo',
					title: 'component.questionnaires.lastSentDate',
					settings: {
						rules: [
							{
								rule: 'min',
								value: 'today',
							},
						],
					},
				},
			],
		},
		{
			type: MagicField.TIMEPICKER,
			key: 'time',
			title: 'component.questionnaires.sendInTime',
			settings: {
				hideRequired: true,
				minuteStep: 5,
			},
		},
	];

	formScheme = [...formSchemePrepend, ...formScheme];

	if (isLoading) {
		return (
			<div className={'py-5'}>
				<Loading />
			</div>
		);
	}

	return (
		<MagicForm
			registerForChanges
			formScheme={formScheme}
			onSubmit={handleSubmit}
			defaultValues={defaultData}
			submitText={'base.sendMonitoring'}
		/>
	);
}
